
import React, { useMemo, useRef } from "react";
import {
	Box,
	Button,
	HStack,
	Spinner,
	Text,
	VStack,
	Link,
	Icon,
} from "@chakra-ui/react";
import { FiRefreshCw } from "react-icons/fi";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import {
	ArtifactProto,
	ExecutionStepProto,
	ResponseMessageProto,
	scenarioOutput_ScenarioStatusToJSON,
} from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON, ExecutionStatusType } from "build-ts/common";
import ReactMarkdown from "react-markdown";
import { FaTableList } from "react-icons/fa6";
import { FaChartLine } from "react-icons/fa";
import ArtifactsList from "./ArtifactsList";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { useNavigate } from "react-router-dom";
import { ChatMessage } from "./Chat";
import { ScheduledJobProto } from "build-ts/schedule";
import { LuWorkflow } from "react-icons/lu";

interface MessageProps {
	author: "user" | "ai";
	content: string;
	responseMessage?: ResponseMessageProto | undefined;
	artifacts?: ArtifactProto[];
	langgraphMessageJsonList?: string[];
	onArtifactClick?: (artifact: ArtifactProto, activeTab?: string, selectedItemIndex?: number) => void;
	chatMessages?: ChatMessage[];
	isLoading: boolean;
	isLastMessage: boolean;
	lsTraceUrl?: string;
	isArtifactsPanelVisible?: boolean;
	allArtifactsForLinks?: ArtifactProto[]; // All artifacts from all messages for dataframe link matching
	userTimestampMs?: number;
	executionStatus?: ExecutionStatusType;
	streamingStatusLabel?: string;
	executionSteps?: ExecutionStepProto[];
	scenarioJobs?: ScheduledJobProto[];
}

/**
 * Transform [[dataframe: table_name]] → markdown link
 */
const transformDataframeLinks = (text: string): string => {
	/**
	 * IDs like spain-ads-cpl-outliers-(spend->-100) and those with &, +, > can
	 * break/truncate markdown link parsing when inserted raw so doing encodeURIComponent
	 */
	return text.replace(
		/\[\[dataframe:\s*([^\]]+)\]\]/gi,
		(_, tableName) => {
			const cleanName = tableName.trim();
			return `[${cleanName}](#dataframe:${encodeURIComponent(cleanName)})`;
		}
	);
};

/**
 * Transform [[workflow: workflow_id]] → markdown link
 */
const transformAnalysisLinks = (text: string): string => {
	/**
	 * IDs like spain-ads-cpl-outliers-(spend->-100) and those with &, +, > can
	 * break/truncate markdown link parsing when inserted raw so doing encodeURIComponent
	*/
	return text.replace(
		/\[\[workflow:\s*([^\]]+)\]\]/gi,
		(_, llmId) => {
			const cleanId = llmId.trim();
			return `[${cleanId}](#workflow:${encodeURIComponent(cleanId)})`;
		}
	);
};

/**
 * Transform [[chart: chart_name]] → markdown link
 */
const transformChartLinks = (text: string): string => {
	return text.replace(
		/\[\[chart:\s*([^\]]+)\]\]/gi,
		(_, chartName) => {
			const cleanName = chartName.trim();
			return `[${cleanName}](#chart:${encodeURIComponent(cleanName)})`;
		}
	);
};

/**
 * Escapes currency-style dollar signs ($36,756) so remark-math doesn't treat
 * them as LaTeX delimiters. Math expressions always start with \, a letter,
 * or a space after $, never directly with a digit.
 */
const escapeCurrencyDollar = (text: string): string => {
	return text.replace(/\$(?=\d)/g, "\\$");
};

function normalizeLatex(markdown: string): string {
	return markdown.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$").replace(/\\\(/g, "$").replace(/\\\)/g, "$");
}

/** Plain JSON from SSE uses string enum names and snake_case; persisted payloads may be normalized by fromJSON. */
function getKgDataframePayload(artifact: ArtifactProto | Record<string, unknown>) {
	const a = artifact as Record<string, unknown>;
	return (a.kg_dataframe ?? a.kgDataframe) as { header?: { id?: string; name?: string } } | undefined;
}

/** Returns true if a DATAFRAME_ANALYSIS artifact contains a pandas_dataframe with the given name. */
function dataframeAnalysisContainsName(artifact: ArtifactProto, name: string): boolean {
	if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) return false;
	const frames = artifact.dataframe_analysis?.pandas_dataframes ?? [];
	return frames.some((f) => f.name?.trim() === name);
}

function dataframeAnalysisContainsChartName(artifact: ArtifactProto, name: string): boolean {
	if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) return false;
	const charts = (
		artifact.dataframe_analysis as unknown as { charts?: Array<{ name?: string }> } | undefined
	)?.charts ?? [];
	return charts.some((c) => c.name?.trim() === name);
}

/** Resolve [[dataframe:name]] to an artifact: checks KG_DATAFRAME by header name and DATAFRAME_ANALYSIS by pandas_dataframes names. */
function findKGDataframeArtifactByTableName(
	tableName: string,
	opts: {
		executionSteps?: ExecutionStepProto[];
		messageArtifacts?: ArtifactProto[];
		allArtifacts?: ArtifactProto[];
		chatMessages?: ChatMessage[];
	},
): ArtifactProto | undefined {
	const normalizedTarget = tableName.trim();

	const matches = (artifact: ArtifactProto | Record<string, unknown>) => {
		const a = artifact as ArtifactProto;
		const type = artifactTypeFromJSON(a.artifact_type);
		if (type === ArtifactType.KG_DATAFRAME) {
			const name = getKgDataframePayload(artifact)?.header?.name?.trim();
			return name === normalizedTarget;
		}
		if (type === ArtifactType.DATAFRAME_ANALYSIS) {
			return dataframeAnalysisContainsName(a, normalizedTarget);
		}
		return false;
	};

	const fromList = (list: ArtifactProto[] | undefined) =>
		list?.find((x) => matches(x)) as ArtifactProto | undefined;

	const steps = opts.executionSteps;
	if (steps?.length) {
		for (let i = steps.length - 1; i >= 0; i--) {
			for (const a of steps[i].artifacts) {
				if (matches(a)) return a;
			}
		}
	}

	let found = fromList(opts.messageArtifacts);
	if (found) return found;

	found = fromList(opts.allArtifacts);
	if (found) return found;

	const msgs = opts.chatMessages;
	for (let i = (msgs?.length ?? 0) - 1; i >= 0; i--) {
		const msg = msgs![i];
		found = fromList(msg.artifacts);
		if (found) return found;
		const st = msg.executionSteps;
		if (st?.length) {
			for (let j = st.length - 1; j >= 0; j--) {
				for (const a of st[j].artifacts) {
					if (matches(a)) return a;
				}
			}
		}
	}

	return undefined;
}

function findChartArtifactByName(
	chartName: string,
	opts: {
		executionSteps?: ExecutionStepProto[];
		messageArtifacts?: ArtifactProto[];
		allArtifacts?: ArtifactProto[];
		chatMessages?: ChatMessage[];
	},
): ArtifactProto | undefined {
	const normalizedTarget = chartName.trim();
	const matches = (artifact: ArtifactProto) =>
		dataframeAnalysisContainsChartName(artifact, normalizedTarget);
	const fromList = (list: ArtifactProto[] | undefined) => list?.find((x) => matches(x));

	const steps = opts.executionSteps;
	if (steps?.length) {
		for (let i = steps.length - 1; i >= 0; i--) {
			for (const a of steps[i].artifacts) {
				if (matches(a)) return a;
			}
		}
	}

	let found = fromList(opts.messageArtifacts);
	if (found) return found;
	found = fromList(opts.allArtifacts);
	if (found) return found;

	for (let i = (opts.chatMessages?.length ?? 0) - 1; i >= 0; i--) {
		const msg = opts.chatMessages?.[i];
		if (!msg) continue;
		found = fromList(msg.artifacts);
		if (found) return found;
		for (let j = (msg.executionSteps?.length ?? 0) - 1; j >= 0; j--) {
			for (const a of msg.executionSteps?.[j]?.artifacts ?? []) {
				if (matches(a)) return a;
			}
		}
	}
	return undefined;
}

function getChartIndexInArtifact(artifact: ArtifactProto, chartName: string): number {
	if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) return -1;
	const charts = (
		artifact.dataframe_analysis as unknown as { charts?: Array<{ name?: string }> } | undefined
	)?.charts ?? [];
	const normalizedTarget = chartName.trim();
	return charts.findIndex((chart) => chart.name?.trim() === normalizedTarget);
}

function getDataframeIndexInArtifact(artifact: ArtifactProto, dataframeName: string): number {
	if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) return -1;
	const dataframes = artifact.dataframe_analysis?.pandas_dataframes ?? [];
	const normalizedTarget = dataframeName.trim();
	return dataframes.findIndex((df) => df.name?.trim() === normalizedTarget);
}

/**
 * Markdown Renderer
 */
export const MarkdownRenderer: React.FC<{
	content: string;
	artifacts?: ArtifactProto[];
	executionSteps?: ExecutionStepProto[];
	isArtifactsPanelVisible?: boolean;
	allArtifactsForLinks?: ArtifactProto[];
	onArtifactClick?: (artifact: ArtifactProto, activeTab?: string, selectedItemIndex?: number) => void;
	chatMessages?: ChatMessage[];
	scenarioJobs?: ScheduledJobProto[];
}> = ({
	content,
	artifacts,
	executionSteps,
	isArtifactsPanelVisible,
	allArtifactsForLinks,
	onArtifactClick,
	chatMessages,
	scenarioJobs,
}) => {
		const baseFontSize = isArtifactsPanelVisible ? "0.8rem" : "1rem";
		const h1Size = isArtifactsPanelVisible ? "1rem" : "1.25rem";
		const h2Size = isArtifactsPanelVisible ? "0.875rem" : "1.125rem";
		const h3Size = isArtifactsPanelVisible ? "0.75rem" : "1rem";
		const h4Size = isArtifactsPanelVisible ? "0.625rem" : "0.875rem";
		const codeFontSize = isArtifactsPanelVisible ? "0.625rem" : "0.875rem";

		// Transform dataframe, chart and analysis links to markdown links
		const processedContent = transformAnalysisLinks(
			transformChartLinks(transformDataframeLinks(content))
		);

		// Normalizing the content for LaTeX and escaping currency-style dollar signs
		const normalizedContent = normalizeLatex(escapeCurrencyDollar(processedContent));

		// Only remount markdown when structural metadata changes (artifacts/steps),
		// not on token text growth; remounting on every content length change is expensive.
		const markdownRemountKey = useMemo(
			() =>
				[
					executionSteps?.length ?? 0,
					artifacts?.length ?? 0,
					allArtifactsForLinks?.length ?? 0,
				].join("-"),
			[
				executionSteps?.length,
				artifacts?.length,
				allArtifactsForLinks?.length,
			],
		);

		const dataframeLinkOptsRef = useRef({
			executionSteps,
			messageArtifacts: artifacts,
			allArtifacts: allArtifactsForLinks,
			chatMessages,
		});
		dataframeLinkOptsRef.current = {
			executionSteps,
			messageArtifacts: artifacts,
			allArtifacts: allArtifactsForLinks,
			chatMessages,
		};

		// Memoize renderers to avoid rebuilding the entire components map every render.
		const markdownComponents = useMemo<any>(
			() => ({
				ul: ({ node, ...props }: any) => (
					<ul style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
				),
				ol: ({ node, ...props }: any) => (
					<ol style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
				),
				li: ({ node, ...props }: any) => (
					<li style={{ fontSize: baseFontSize, marginBottom: "0.15rem", fontWeight: 500 }} {...props} />
				),
				p: ({ node, ...props }: any) => (
					<p style={{ fontSize: baseFontSize, marginTop: "0.5rem", marginBottom: "0.5rem", fontWeight: 500 }} {...props} />
				),
				strong: ({ node, ...props }: any) => (
					<strong style={{ fontWeight: 650 }} {...props} />
				),
				h1: ({ node, ...props }: any) => (
					<h1 style={{ fontSize: h1Size, fontWeight: "bold", margin: "1rem 0" }} {...props} />
				),
				h2: ({ node, ...props }: any) => (
					<h2 style={{ fontSize: h2Size, fontWeight: "bold", margin: "0.875rem 0" }} {...props} />
				),
				h3: ({ node, ...props }: any) => (
					<h3 style={{ fontSize: h3Size, fontWeight: "bold", margin: "0.75rem 0" }} {...props} />
				),
				h4: ({ node, ...props }: any) => (
					<h4 style={{ fontSize: h4Size, fontWeight: "bold", margin: "0.625rem 0" }} {...props} />
				),
				a: ({ href, children }: any) => {
					if (href?.startsWith("#dataframe:")) {
						const encodedTableName = href.replace("#dataframe:", "");
						let tableName = encodedTableName;
						try {
							tableName = decodeURIComponent(encodedTableName);
						} catch {
							// Keep raw value if decode fails for any malformed legacy links.
						}

						const matchingArtifact = findKGDataframeArtifactByTableName(tableName, {
							executionSteps,
							messageArtifacts: artifacts,
							allArtifacts: allArtifactsForLinks,
							chatMessages,
						});

						return (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									const resolved = findKGDataframeArtifactByTableName(
										tableName,
										dataframeLinkOptsRef.current,
									);
									if (resolved && onArtifactClick) {
										const selectedDfIndex = getDataframeIndexInArtifact(resolved, tableName);
										onArtifactClick(
											resolved,
											"data",
											selectedDfIndex >= 0 ? selectedDfIndex : 0,
										);
									}
								}}
								style={{
									color: "#3182ce",
									cursor: matchingArtifact ? "pointer" : "default",
									textDecoration: "none",
									fontWeight: 650,
									display: "inline-flex",
									alignItems: "center",
									gap: "0.25rem",
								}}
								onMouseEnter={(e) => {
									if (matchingArtifact) {
										e.currentTarget.style.textDecoration = "underline";
									}
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.textDecoration = "none";
								}}
							>
								<span style={{ margin: 0, display: "inline", lineHeight: "inherit" }}>
									<Icon as={FaTableList} display="inline" flexShrink={0} boxSize={4} mr={1} />
									{children}
								</span>
							</a>
						);
					}

					if (href?.startsWith("#workflow:")) {
						const encodedLLMId = href.replace("#workflow:", "");
						let llmId = encodedLLMId;
						try {
							llmId = decodeURIComponent(encodedLLMId);
						} catch {
							// Keep raw value if decode fails for any malformed legacy links.
						}

						const isScenarioJob = (a: ArtifactProto) =>
							artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB;

						let matchingArtifact: ArtifactProto | undefined;

						matchingArtifact = artifacts?.find(
							(a) =>
								isScenarioJob(a) &&
								((a.scenario_job?.scheduled_analysis as { darwin_name?: string } | undefined)
									?.darwin_name === llmId)
						);
						if (!matchingArtifact) {
							for (let i = (chatMessages?.length ?? 0) - 1; i >= 0; i--) {
								const msg = chatMessages?.[i];
								if (!msg) continue;
								matchingArtifact = msg?.artifacts?.find(
									(a) =>
										isScenarioJob(a) &&
										((a.scenario_job?.scheduled_analysis as { darwin_name?: string } | undefined)
											?.darwin_name === llmId)
								);
								if (matchingArtifact) break;
							}
						}
						if (!matchingArtifact) {
							const job = scenarioJobs?.find(
								(j) =>
								((j.scheduled_analysis as { darwin_name?: string } | undefined)
									?.darwin_name === llmId)
							);
							if (job) matchingArtifact = { artifact_type: ArtifactType.SCENARIO_JOB, scenario_job: job };
						}

						const displayName =
							matchingArtifact?.scenario_job?.header?.name || llmId;

						return (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									if (matchingArtifact && onArtifactClick) {
										onArtifactClick(matchingArtifact);
									}
								}}
								style={{
									color: "#3182ce",
									cursor: matchingArtifact ? "pointer" : "default",
									textDecoration: "none",
									fontWeight: 650,
									display: "inline-flex",
									alignItems: "center",
									gap: "0.25rem",
								}}
								onMouseEnter={(e) => {
									if (matchingArtifact) {
										e.currentTarget.style.textDecoration = "underline";
									}
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.textDecoration = "none";
								}}
							>
								<span style={{ margin: 0, display: "inline", lineHeight: "inherit" }}>
									<Icon as={LuWorkflow} style={{ display: "inline", flexShrink: 0 }} boxSize={5} mr={1} />
									{displayName}
								</span>
							</a>
						);
					}
					if (href?.startsWith("#chart:")) {
						const encodedChartName = href.replace("#chart:", "");
						let chartName = encodedChartName;
						try {
							chartName = decodeURIComponent(encodedChartName);
						} catch {
							// Keep raw value if decode fails for malformed links.
						}
						const matchingArtifact = findChartArtifactByName(chartName, {
							executionSteps,
							messageArtifacts: artifacts,
							allArtifacts: allArtifactsForLinks,
							chatMessages,
						});
						return (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									const resolved = findChartArtifactByName(chartName, {
										...dataframeLinkOptsRef.current,
									});
									if (resolved && onArtifactClick) {
										const selectedChartIndex = getChartIndexInArtifact(resolved, chartName);
										onArtifactClick(resolved, "charts", selectedChartIndex >= 0 ? selectedChartIndex : 0);
									}
								}}
								style={{
									color: "#3182ce",
									cursor: matchingArtifact ? "pointer" : "default",
									textDecoration: "none",
									fontWeight: 650,
									display: "inline-flex",
									alignItems: "center",
									gap: "0.25rem",
								}}
							>
								<span style={{ margin: 0, display: "inline", lineHeight: "inherit" }}>
									<Icon as={FaChartLine} display="inline" flexShrink={0} boxSize={4} mr={1} />
									{children}
								</span>
							</a>
						);
					}

					return (
						<a
							href={href}
							target="_blank"
							rel="noopener noreferrer"
							style={{
								color: "#3182ce",
								textDecoration: "underline",
								wordBreak: "break-all",
							}}
						>
							{children}
						</a>
					);
				},
				pre: ({ children }: any) => (
					<pre style={{
						backgroundColor: "bg.surface",
						color: "text.primary",
						padding: "0 0 0 1rem",
						borderRadius: "8px",
						overflowX: "auto",
						margin: "0.75rem 0",
						fontSize: codeFontSize,
						lineHeight: 1.5,
						whiteSpace: "pre-wrap",
						wordBreak: "break-word",
					}}>
						{children}
					</pre>
				),
				table: ({ children }: any) => (
					<div style={{
						overflowX: "auto",
						marginTop: "1rem",
						marginBottom: "1rem",
						maxWidth: "100%",
					}}>
						<table style={{
							borderCollapse: "collapse",
							border: "1px solid #d1d5db",
							minWidth: "100%",
							whiteSpace: "nowrap",
							fontSize: codeFontSize,
						}}>
							{children}
						</table>
					</div>
				),
				thead: ({ children }: any) => (
					<thead style={{
						backgroundColor: "#f3f4f6",
					}}>
						{children}
					</thead>
				),
				th: ({ children }: any) => (
					<th style={{
						border: "1px solid #d1d5db",
						padding: "0.5rem 1rem",
						textAlign: "left",
						fontWeight: 600,
						backgroundColor: "#f3f4f6",
					}}>
						{children}
					</th>
				),
				td: ({ children }: any) => (
					<td style={{
						border: "1px solid #d1d5db",
						padding: "0.5rem 1rem",
						textAlign: "left",
					}}>
						{children}
					</td>
				),
			}),
			[
				baseFontSize,
				h1Size,
				h2Size,
				h3Size,
				h4Size,
				codeFontSize,
				executionSteps,
				artifacts,
				allArtifactsForLinks,
				chatMessages,
				onArtifactClick,
				scenarioJobs,
			],
		);

		return (
			<Box
				textAlign="left"
				ml={0}
				className="markdown-content"
				w="100%"
				maxW="100%"
				minW="0"
				sx={{
					wordBreak: "break-word",
					overflowWrap: "break-word",
					"& *": { maxWidth: "100%" },
				}}
			>
				<ReactMarkdown
					key={markdownRemountKey}
					remarkPlugins={[remarkGfm, remarkMath]}
					rehypePlugins={[rehypeKatex]}
					components={markdownComponents}
				>
					{normalizedContent}
				</ReactMarkdown>
			</Box>
		);
	};

const MessageComponent: React.FC<MessageProps> = ({
	author,
	content,
	responseMessage,
	artifacts,
	langgraphMessageJsonList,
	onArtifactClick,
	chatMessages,
	isLoading,
	isLastMessage,
	lsTraceUrl,
	isArtifactsPanelVisible,
	allArtifactsForLinks,
	userTimestampMs,
	executionStatus,
	scenarioJobs,
	streamingStatusLabel = "Thinking ...",
	executionSteps,
}) => {
	const { defaultTimeZone } = useUser();
	const navigate = useNavigate();
	const mountTimeRef = useRef(Date.now());

	// Preserve fallback timestamp across renders so user-message props remain stable.
	const formattedUserTime = defaultTimeZone
		? epochConverterWithTimeZone(userTimestampMs ?? mountTimeRef.current, "h:mm a, d MMM", defaultTimeZone)
		: undefined;

	const isAiStreaming = author === "ai" && isLastMessage && isLoading;
	const showReasoningPanel =
		author === "ai" && ((executionSteps?.length ?? 0) > 0 || isAiStreaming);
	const hasStreamingAssistantText = Boolean(
		(content?.trim()?.length ?? 0) > 0 ||
		(responseMessage?.scenario_output?.findings?.trim()?.length ?? 0) > 0 ||
		(responseMessage?.scenario_synthesis_output?.output_string?.trim()?.length ?? 0) > 0,
	);

	return (
		<HStack
			alignSelf={author === "user" ? "flex-end" : "flex-start"}
			w={author === "user" ? "auto" : "100%"}
		>
			<VStack align={author === "user" ? "flex-end" : "flex-start"} spacing={0} w={author === "user" ? "auto" : "100%"}>
				<Box
					bg={author === "user" ? "purple.50" : "white"}
					px={3}
					py={2}
					borderRadius="lg"
					w={author === "ai" ? "100%" : "auto"}
					maxW="100%"
					flex="1"
					ml={author === "user" ? "3" : "0"}
					mb={author === "user" && formattedUserTime ? 0 : 3}
				>
					{isLoading && isLastMessage && !content && !responseMessage ? (
						author === "ai" ? (
							<Box w="100%">
								<ArtifactsList
									onArtifactClick={onArtifactClick}
									executionSteps={executionSteps}
									isStreaming
									hasStreamingAssistantText={hasStreamingAssistantText}
								/>
							</Box>
						) : (
							<HStack spacing={2} align="center">
								<Spinner size="sm" color="purple.500" thickness="2px" />
								<Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} color="gray.600" mb={0}>
									{streamingStatusLabel}
								</Text>
							</HStack>
						)
					) : executionStatus === ExecutionStatusType.TOOL_CALL_FAILURE ? (
						// Error state
						<VStack align="start" spacing={3} py={1}>
							<HStack spacing={2}>
								<Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} color="red.500" fontWeight="600" mb={0}>
									An error occurred. Please start a new chat.
								</Text>
							</HStack>
							<Button
								size="sm"
								colorScheme="purple"
								leftIcon={<Icon as={FiRefreshCw} boxSize={3.5} />}
								onClick={() => navigate("/chat/new")}
								fontWeight="600"
							>
								Start New Chat
							</Button>
						</VStack>
					) : (
						// Normal message state
						<VStack align="start" spacing={0} w="100%">
							{author === "ai" && (
								<VStack w="100%">
									<HStack w="100%" justify="flex-end">
										{/* LangSmith trace link for Eva */}
										{lsTraceUrl && (
											<Link
												href={lsTraceUrl}
												isExternal
												fontSize={isArtifactsPanelVisible ? "xs" : "sm"}
												color="purple.600"
												fontWeight="bold"
												_hover={{ color: "purple.700", textDecoration: "none" }}
											>
												Trace
											</Link>
										)}
									</HStack>
								</VStack>
							)}
							{author === "ai" && responseMessage?.scenario_output && (
								<VStack align="start" spacing={2} w="100%" mt={2}>
									{showReasoningPanel ? (
										<Box w="100%">
											<ArtifactsList
												onArtifactClick={onArtifactClick}
												executionSteps={executionSteps}
												isStreaming={isAiStreaming}
												hasStreamingAssistantText={hasStreamingAssistantText}
											/>
										</Box>
									) : null}
									<Box textAlign="left">
										<Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} fontWeight="bold" mb={1}>
											Status: {responseMessage.scenario_output.status !== undefined
												? scenarioOutput_ScenarioStatusToJSON(responseMessage.scenario_output.status)
												: "N/A"}
										</Text>
									</Box>
									<Box>
										<Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} fontWeight="bold" mb={1} textAlign="left">
											Findings:
										</Text>
										<Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} m={0} textAlign="left">
											<MarkdownRenderer
												content={responseMessage.scenario_output.findings || "N/A"}
												artifacts={artifacts}
												executionSteps={executionSteps}
												isArtifactsPanelVisible={isArtifactsPanelVisible}
												allArtifactsForLinks={allArtifactsForLinks}
												onArtifactClick={onArtifactClick}
												chatMessages={chatMessages}
												scenarioJobs={scenarioJobs}
											/>
										</Text>
									</Box>
								</VStack>
							)}
							{author === "ai" && responseMessage?.scenario_synthesis_output && (
								<VStack align="start" spacing={2} w="100%" mt={2}>
									{showReasoningPanel ? (
										<Box w="100%">
											<ArtifactsList
												onArtifactClick={onArtifactClick}
												executionSteps={executionSteps}
												isStreaming={isAiStreaming}
												hasStreamingAssistantText={hasStreamingAssistantText}
											/>
										</Box>
									) : null}
									<Box>
										<Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} fontWeight="bold" mb={1} textAlign="left">
											Output String:
										</Text>
										<Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} m={0} textAlign="left">
											<MarkdownRenderer
												content={responseMessage.scenario_synthesis_output.output_string || "N/A"}
												artifacts={artifacts}
												executionSteps={executionSteps}
												isArtifactsPanelVisible={isArtifactsPanelVisible}
												allArtifactsForLinks={allArtifactsForLinks}
												onArtifactClick={onArtifactClick}
												chatMessages={chatMessages}
												scenarioJobs={scenarioJobs}
											/>
										</Text>
									</Box>
									<Box>
										<Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} fontWeight="bold" mb={1} textAlign="left">
											Notify:
										</Text>
										<Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} m={0} textAlign="left">
											{responseMessage.scenario_synthesis_output.notify ? "Yes" : "No"}
										</Text>
									</Box>
								</VStack>
							)}
							{author === "ai" && !responseMessage?.scenario_output && !responseMessage?.scenario_synthesis_output && (
								<VStack align="start" spacing={1} w="100%">
									{showReasoningPanel ? (
										<Box w="100%">
											<ArtifactsList
												onArtifactClick={onArtifactClick}
												executionSteps={executionSteps}
												isStreaming={isAiStreaming}
												hasStreamingAssistantText={hasStreamingAssistantText}
											/>
										</Box>
									) : null}
									<Box w="100%" mx={isArtifactsPanelVisible ? 1 : 2}>
										<MarkdownRenderer
											content={content}
											artifacts={artifacts}
											executionSteps={executionSteps}
											isArtifactsPanelVisible={isArtifactsPanelVisible}
											allArtifactsForLinks={allArtifactsForLinks}
											onArtifactClick={onArtifactClick}
											chatMessages={chatMessages}
											scenarioJobs={scenarioJobs}
										/>
									</Box>
								</VStack>
							)}
							{author === "user" && (
								<Text mb={0} alignSelf="flex-end" fontSize={isArtifactsPanelVisible ? "sm" : "md"} textAlign="left" fontWeight={500}>
									{/* Right now, removing [[workflow: name]] tags from the user content  and replace with the name */}
									{/* Will be replaced this when scenario name is sanitized and a link to it */}
									{content.split(/\[\[workflow:\s*([^\]]+)\]\]/gi).map((part, i) =>
										i % 2 === 0 ? part : (
											<span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontWeight: 700, color: "var(--chakra-colors-purple-600)" }}>
												<Icon as={LuWorkflow} display="inline" boxSize="1em" flexShrink={0} />
												{part.trim()}
											</span>
										)
									)}
								</Text>
							)}
						</VStack>
					)}
				</Box>
				{author === "user" && formattedUserTime && (
					<Text
						fontSize="xs"
						color="gray.400"
						textAlign="right"
						mb={3}
						mt={0.5}
						ml={3}
						alignSelf="flex-end"
						w="100%"
					>
						{formattedUserTime}
					</Text>
				)}
			</VStack>
		</HStack>
	);
};

export default React.memo(MessageComponent);